// src/components/admin/ImageUpload.jsx
import React, { useState, useRef } from "react";
import { FiUpload, FiX, FiLoader } from "react-icons/fi";
import { aPost, aDelete } from "../../utils/adminApi";

export default function ImageUpload({
  currentImage,
  onImageUploaded,
  onImageRemoved,
  label = "Product Image",
  multiple = false,
  onMultipleImagesUploaded,
  existingImages = [],
  productId = null,
}) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const normalizeUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/uploads"))
      return `https://lightslategrey-mole-360881.hostingersite.com${url}`;
    return `https://lightslategrey-mole-360881.hostingersite.com/uploads/products/${url}`;
  };

  const processFiles = async (files) => {
    if (files.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();

      if (multiple) {
        files.forEach((file) => formData.append("images", file));
        // Use the correct endpoint
        const response = await aPost("/upload/product-images", formData);

        if (response.success && response.images) {
          if (onMultipleImagesUploaded) {
            onMultipleImagesUploaded(response.images);
          }
        } else {
          throw new Error(response.message || "Upload failed");
        }
      } else {
        formData.append("image", files[0]);
        // Use the correct endpoint
        const response = await aPost("/upload/product-image", formData);

        if (response.success && response.imageUrl) {
          if (onImageUploaded) {
            onImageUploaded(response.imageUrl);
          }
        } else {
          throw new Error(response.message || "Upload failed");
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    await processFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  };

  // src/components/admin/ImageUpload.jsx - Fix the delete function

  const handleRemoveImage = async () => {
    if (!currentImage) return;
    if (!window.confirm("Are you sure you want to remove this image?")) return;

    try {
      let filename = currentImage;
      if (currentImage.includes("/uploads/")) {
        filename = currentImage.split("/uploads/").pop();
      } else if (currentImage.includes("https://lightslategrey-mole-360881.hostingersite.com")) {
        filename = currentImage.split("/").pop();
      }
      filename = filename.split("?")[0];

      await aDelete("/upload/image", {
        imageUrl: filename,
        productId,        // ← pass productId
        field: "image",   // ← main image field
      });

      if (onImageRemoved) onImageRemoved();
    } catch (error) {
      console.error("Delete error:", error);
      alert(error.message || "Failed to delete image");
    }
  };

  const handleRemoveMultipleImage = async (imageUrl, index) => {
    if (!window.confirm("Are you sure you want to remove this image?")) return;

    try {
      let filename = imageUrl;
      if (imageUrl.includes("/uploads/")) {
        filename = imageUrl.split("/uploads/").pop();
      } else if (imageUrl.includes("https://lightslategrey-mole-360881.hostingersite.com")) {
        filename = imageUrl.split("/").pop();
      }
      filename = filename.split("?")[0];

      await aDelete("/upload/image", {
        imageUrl: filename,
        productId,           // ← from props
        field: "images",     // ← additional images field
      });

      if (onMultipleImagesUploaded) {
        const newImages = existingImages.filter((_, i) => i !== index);
        onMultipleImagesUploaded(newImages);
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert(error.message || "Failed to delete image");
    }
  };

  const displayedImage = currentImage ? normalizeUrl(currentImage) : null;

  return (
    <div className="image-upload-container" style={{ marginBottom: "20px" }}>
      <label
        style={{
          display: "block",
          marginBottom: "8px",
          fontWeight: "bold",
          fontSize: "13px",
        }}
      >
        {label}
      </label>

      {error && (
        <div
          style={{
            background: "#fee",
            color: "#c00",
            padding: "8px",
            borderRadius: "4px",
            marginBottom: "10px",
            fontSize: "12px",
          }}
        >
          ❌ Error: {error}
        </div>
      )}

      {!multiple && displayedImage ? (
        <div style={{ position: "relative", display: "inline-block" }}>
          <img
            src={displayedImage}
            alt="Uploaded"
            style={{
              width: "200px",
              height: "200px",
              objectFit: "cover",
              borderRadius: "8px",
              border: "1px solid #ddd",
            }}
            onError={(e) => {
              console.error("Image failed to load:", displayedImage);
              e.target.style.display = "none";
              setError("Image failed to load. Please re-upload.");
            }}
          />
          <button
            type="button"
            onClick={handleRemoveImage}
            style={{
              position: "absolute",
              top: "-10px",
              right: "-10px",
              background: "#e74c3c",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "28px",
              height: "28px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiX />
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragActive ? "#3498db" : "#ddd"}`,
            borderRadius: "8px",
            padding: multiple ? "20px" : "30px",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.3s",
            background: dragActive ? "#f0f8ff" : "#fafafa",
          }}
        >
          {uploading ? (
            <div>
              <FiLoader
                style={{
                  animation: "spin 1s linear infinite",
                  fontSize: "32px",
                  marginBottom: "10px",
                }}
              />
              <div>Uploading...</div>
            </div>
          ) : (
            <>
              <FiUpload
                size={multiple ? 24 : 32}
                style={{ marginBottom: "10px", color: "#999" }}
              />
              <div style={{ fontSize: multiple ? "12px" : "14px" }}>
                {multiple
                  ? "Click or drag images here"
                  : "Click or drag to upload image"}
              </div>
              <small style={{ color: "#999", fontSize: "11px" }}>
                PNG, JPG, GIF up to 5MB
              </small>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
    </div>
  );
}
